import { useState } from "react";
import "../../style/scss/App.scss";
import { Form, FormActions, FormInput, FormCheckbox, FormSelect, FormDate } from "../Controls/Form";
import Dialog from "../Controls/Dialog";
import CustomButton from "../Controls/CustomButton";
import BreadCrumb from "../Controls/BreadCrumb";
import UserData from "../Controls/UserData";
import API from "@src/API.js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const WORK_AUTH_OPTIONS = [
  { value: 0, label: "Prefer not to say" },
  { value: 1, label: "Citizen" },
  { value: 2, label: "Permanent resident" },
  { value: 3, label: "Work visa / permit" },
  { value: 4, label: "Need sponsorship" },
];

const emptyPwForm = () => ({
  oldPassword: "",
  password: "",
  passwordConfirm: "",
});

export default function PersonEdit() {
  const navigate = useNavigate();
  const userData = UserData.UserData() || {};
  const personData = userData.person || {};
  const lookups = UserData.Lookups() || {};
  const countries = (lookups.Countries || lookups.countries || []).map((c) => ({
    value: c.Value ?? c.value ?? c.ID ?? c.id,
    label: c.Key ?? c.key ?? c.Name ?? c.name ?? String(c.Value ?? c.value ?? ""),
  }));

  const [person, updatePerson] = useState({
    FirstName: personData.FirstName || personData.firstName || "",
    LastName: personData.LastName || personData.lastName || "",
    NickName: personData.NickName || personData.nickName || "",
    CitizenshipCountryID: personData.CitizenshipCountryID ?? personData.citizenshipCountryID ?? "",
    WorkAuthorizationType: personData.WorkAuthorizationType ?? personData.workAuthorizationType ?? 0,
    WorkAuthorizationExpires:
      personData.WorkAuthorizationExpires ?? personData.workAuthorizationExpires ?? null,
    ShareWorkAuthForMatching:
      personData.ShareWorkAuthForMatching ?? personData.shareWorkAuthForMatching ?? false,
    MustChangePassword:
      personData.MustChangePassword ?? personData.mustChangePassword ?? false,
  });
  const isSysAdmin = !!(personData.IsSysAdmin ?? personData.isSysAdmin);
  const [busy, setBusy] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwForm, setPwForm] = useState(emptyPwForm);
  const [pwError, setPwError] = useState("");

  function Return() {
    navigate("/account");
  }

  function openPwModal() {
    setPwForm(emptyPwForm());
    setPwError("");
    setPwOpen(true);
  }

  function closePwModal() {
    if (pwBusy) return;
    setPwOpen(false);
    setPwForm(emptyPwForm());
    setPwError("");
  }

  function onPwFieldChange(e) {
    const { name, value } = e.target;
    setPwForm((prev) => ({ ...prev, [name]: value }));
    if (pwError) setPwError("");
  }

  async function onToggleMustChangePassword(checked) {
    if (!isSysAdmin || flagBusy) return;
    const personId = personData.ID ?? personData.id ?? userData.userId;
    if (!personId) return;
    setFlagBusy(true);
    try {
      const resp = await API.postAsync("/Person/MustChangePassword", {
        personId: Number(personId),
        mustChangePassword: !!checked,
      });
      if (resp?.ok) {
        updatePerson((prev) => ({ ...prev, MustChangePassword: !!checked }));
        toast.success(checked ? "Password change required on next login" : "Password change requirement cleared");
        await UserData.GetUserDataAsync();
      } else {
        toast.error(resp?.jsonData?.message || "Could not update flag");
      }
    } catch (err) {
      toast.error(err?.message || "Could not update flag");
    } finally {
      setFlagBusy(false);
    }
  }

  async function onChangePasswordAsync(e) {
    e?.preventDefault?.();
    if (pwBusy) return;
    const { oldPassword, password, passwordConfirm } = pwForm;
    if (!oldPassword || !password || !passwordConfirm) {
      setPwError("All password fields are required");
      return;
    }
    if (password !== passwordConfirm) {
      setPwError("New password and confirmation do not match");
      return;
    }
    setPwBusy(true);
    setPwError("");
    try {
      const resp = await API.postAsync("/Person/ChangePassword", {
        oldPassword,
        password,
        passwordConfirm,
      });
      if (resp?.ok) {
        toast.success("Password changed");
        setPwOpen(false);
        setPwForm(emptyPwForm());
      } else {
        setPwError(resp?.jsonData?.message || "Could not change password");
      }
    } catch (err) {
      setPwError(err?.message || "Could not change password");
    } finally {
      setPwBusy(false);
    }
  }

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        FirstName: person.FirstName,
        LastName: person.LastName,
        NickName: person.NickName,
        CitizenshipCountryID: person.CitizenshipCountryID
          ? Number(person.CitizenshipCountryID)
          : null,
        WorkAuthorizationType: Number(person.WorkAuthorizationType || 0),
        WorkAuthorizationExpires: person.WorkAuthorizationExpires || null,
        ShareWorkAuthForMatching: !!person.ShareWorkAuthForMatching,
      };
      const resp = await API.postAsync("/Person/WorkAuthorization", payload);
      if (resp?.ok) {
        toast.success("Personal info saved");
        await UserData.GetUserDataAsync();
        Return();
      } else {
        toast.error(resp?.jsonData?.message || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pagebody">
      <BreadCrumb props={[{ route: "/account", label: "Account" }, { label: "Personal info" }]} />
      <div className="tabbody">
        <h1>Personal info</h1>
        <Form object={person} onUpdate={updatePerson}>
          <FormInput field="FirstName" label="First name" required />
          <FormInput field="LastName" label="Last name" required />
          <FormInput field="NickName" label="Nickname" />
          <FormSelect
            field="CitizenshipCountryID"
            label="Citizenship country"
            valueProp="value"
            labelProp="label"
            options={[{ value: "", label: "Not specified" }, ...countries]}
          />
          <FormSelect
            field="WorkAuthorizationType"
            label="Work authorization"
            valueProp="value"
            labelProp="label"
            options={WORK_AUTH_OPTIONS}
          />
          <FormDate field="WorkAuthorizationExpires" label="Authorization expires (if applicable)" />
          <FormCheckbox
            field="ShareWorkAuthForMatching"
            label="Allow matching to use my work-authorization status"
          />
          <p className="fieldHint">
            Off by default. When enabled, matching may soft-rank citizenship country and sponsorship
            needs for onsite roles. Recruiters never see more than this consent allows.
          </p>
          {isSysAdmin ? (
            <div className="field form-group" style={{ marginTop: "1rem" }}>
              <label className="form-label">
                <input
                  type="checkbox"
                  checked={!!person.MustChangePassword}
                  disabled={flagBusy}
                  data-testid="must-change-password-toggle"
                  onChange={(e) => onToggleMustChangePassword(e.target.checked)}
                />{" "}
                Require password change on next login
              </label>
            </div>
          ) : null}
          <FormActions
            onSaveAsync={async () => {
              await onDoneAsync();
            }}
            onCancelAsync={async () => {
              Return();
            }}
            doneLabel="Save"
          />
        </Form>
        <p className="fieldHint" style={{ marginTop: "1.5rem" }}>
          <CustomButton variant="contained" color="secondary" onClick={openPwModal}>
            Change password
          </CustomButton>
        </p>
      </div>

      <Dialog
        open={pwOpen}
        onClose={closePwModal}
        title="Change Password"
        actions={
          <>
            <CustomButton variant="outlined" color="secondary" onClick={closePwModal} disabled={pwBusy}>
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={onChangePasswordAsync}
              disabled={pwBusy}
            >
              Change password
            </CustomButton>
          </>
        }
      >
        {/* Controlled inputs — Form/FormInput keep local state and only sync via formObject,
            which left pwForm empty on submit (and toast has no ToastContainer, so it looked like a no-op). */}
        <div className="changePasswordForm">
          <div className="field form-group">
            <label htmlFor="oldPassword" className="form-label">
              Current password
            </label>
            <input
              id="oldPassword"
              name="oldPassword"
              type="password"
              className="form-input"
              autoComplete="current-password"
              value={pwForm.oldPassword}
              onChange={onPwFieldChange}
              disabled={pwBusy}
              required
            />
          </div>
          <div className="field form-group">
            <label htmlFor="password" className="form-label">
              New password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              className="form-input"
              autoComplete="new-password"
              value={pwForm.password}
              onChange={onPwFieldChange}
              disabled={pwBusy}
              required
            />
          </div>
          <div className="field form-group">
            <label htmlFor="passwordConfirm" className="form-label">
              Confirm new password
            </label>
            <input
              id="passwordConfirm"
              name="passwordConfirm"
              type="password"
              className="form-input"
              autoComplete="new-password"
              value={pwForm.passwordConfirm}
              onChange={onPwFieldChange}
              disabled={pwBusy}
              required
            />
          </div>
          {pwError ? <p className="error-text" role="alert">{pwError}</p> : null}
        </div>
      </Dialog>
    </div>
  );
}
